/*
 * Bakeology Studios — motion layer + sticky add-to-cart
 * =====================================================
 * Design position: premium reads as restraint. Graza ships six keyframes and
 * no animation library; Lune Croissanterie ships two and none. So this file
 * deliberately contains no scroll-hijacking, no parallax, and no entrance
 * animation on product grids. Every motion here does a conversion job:
 *
 *   1. Reveal-on-scroll — TRUST CONTENT ONLY (reviews, process, guarantees)
 *   2. Add-to-cart confirmation — acknowledge the most important click
 *   3. Second-image swap on product cards — answer "what's inside / how big"
 *   4. Sticky add-to-cart with an animated running total — dissolve price shock
 *
 * Everything animates only `transform` and `opacity` (compositor-only, no
 * main-thread layout) and everything is gated behind
 * `prefers-reduced-motion: no-preference` — motion is opted into, not forced.
 *
 * THEME GOTCHA, verified in-browser 28 Jul 2026: native smooth scrolling is a
 * silent no-op on the product page. Both scrollIntoView({behavior:'smooth'})
 * and window.scrollTo({behavior:'smooth'}) leave scrollY completely unchanged,
 * and a hand-rolled rAF animation gets undone because the page lazy-loads a
 * related-products row mid-flight. Nothing here depends on smooth scrolling.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STYLE_ID = 'bk-motion-styles';

  /* ---------------------------------------------------------------- styles */

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = [
      /* 1. reveal-on-scroll (trust content only) */
      '.bk-reveal{opacity:0;transform:translateY(16px);',
      'transition:opacity 260ms cubic-bezier(.2,0,0,1),transform 260ms cubic-bezier(.2,0,0,1);',
      'will-change:opacity,transform}',
      '.bk-reveal.bk-in{opacity:1;transform:none}',

      /* 2. add-to-cart confirmation */
      '@keyframes bk-atc-press{0%{transform:scale(1)}38%{transform:scale(.965)}100%{transform:scale(1)}}',
      '.bk-atc-confirm{animation:bk-atc-press 180ms cubic-bezier(.2,0,0,1)}',
      '@keyframes bk-cart-bump{0%{transform:scale(1)}40%{transform:scale(1.22)}100%{transform:scale(1)}}',
      '.bk-cart-bump{animation:bk-cart-bump 320ms cubic-bezier(.2,0,0,1)}',

      /* 4. sticky add-to-cart bar */
      '#bk-sticky-atc{position:fixed;left:0;right:0;bottom:0;z-index:99990;',
      'background:#0E3A27;color:#E8E0D0;border-top:1px solid rgba(232,224,208,.18);',
      'padding:10px 14px calc(10px + env(safe-area-inset-bottom,0px));',
      'display:flex;align-items:center;gap:14px;',
      'transform:translateY(102%);transition:transform 240ms cubic-bezier(.2,0,0,1);',
      'box-shadow:0 -6px 24px rgba(0,0,0,.18)}',
      '#bk-sticky-atc.bk-show{transform:none}',
      '#bk-sticky-atc .bk-sa-meta{flex:1 1 auto;min-width:0;line-height:1.25}',
      '#bk-sticky-atc .bk-sa-name{display:block;font-size:13px;letter-spacing:.02em;',
      'color:rgba(232,224,208,.72);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
      '#bk-sticky-atc .bk-sa-total{display:block;font-size:19px;font-weight:500;letter-spacing:.01em;',
      'font-variant-numeric:tabular-nums}',
      '#bk-sticky-atc .bk-sa-btn{flex:0 0 auto;background:#E8E0D0;color:#0E3A27;border:0;',
      'min-height:48px;padding:0 26px;border-radius:999px;font:inherit;font-size:14px;',
      'font-weight:600;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;',
      'transition:opacity 140ms ease}',
      '#bk-sticky-atc .bk-sa-btn:active{opacity:.86}',
      '@media (min-width:750px){#bk-sticky-atc{padding-inline:32px}}',

      /* honour reduced motion everywhere */
      '@media (prefers-reduced-motion:reduce){',
      '.bk-reveal{opacity:1;transform:none;transition:none}',
      '.bk-atc-confirm,.bk-cart-bump{animation:none}',
      '#bk-sticky-atc{transition:none}}'
    ].join('');
    document.head.appendChild(s);
  }

  /* ------------------------------------------------- 1. reveal on scroll */
  /* Applied ONLY to trust content. Never to product grids — a customer
     scanning for a cake should not have to wait for cakes to appear.        */

  var TRUST_SELECTORS = [
    '[id*="testimonial" i]',
    '[class*="testimonial" i]',
    '.ss-feature-1',
    '[id*="image_with_text"]',
    '.image-with-text'
  ];

  function initReveal() {
    if (REDUCED || !('IntersectionObserver' in window)) return;
    var nodes = [];
    TRUST_SELECTORS.forEach(function (sel) {
      try {
        [].forEach.call(document.querySelectorAll(sel), function (el) {
          if (nodes.indexOf(el) !== -1) return;
          var r = el.getBoundingClientRect();
          // only top-level trust sections, and only if reasonably tall
          if (r.height <= 80) return;
          // Never animate an element wider than the viewport. A marquee or
          // carousel TRACK is one continuous strip several thousand pixels
          // wide — it is not a "trust section", and hiding it hides every
          // card inside it. See the threshold note below for how this
          // actually broke the homepage testimonials on phones.
          if (r.width > window.innerWidth * 1.5) return;
          nodes.push(el);
        });
      } catch (e) { /* invalid selector in an old browser — skip */ }
    });
    if (!nodes.length) return;

    // threshold MUST stay 0.
    //
    // This was 0.15, and it silently blanked the homepage testimonials on
    // every phone while working perfectly on desktop. IntersectionObserver
    // measures the visible fraction of the ELEMENT, not of the screen. The
    // testimonials track is ~5400px wide, so on a 390px phone at most ~7% of
    // it can ever be on screen — under 0.15, so `bk-in` was never added and
    // `.bk-reveal{opacity:0}` held forever. On a 1440px desktop the same
    // element reaches ~27% and reveals fine. Any element larger than the
    // viewport hits this, and it fails closed: the content stays invisible.
    //
    // threshold 0 + a negative bottom rootMargin gives the same "reveal just
    // after it comes into view" feel, but keys off position rather than a
    // ratio, so element size cannot break it.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('bk-in');
        io.unobserve(entry.target); // one-shot; never re-animate
      });
    }, { threshold: 0, rootMargin: '0px 0px -12% 0px' });

    nodes.forEach(function (el) {
      // Don't hide anything already on screen at load — that would delay
      // first meaningful paint for above-the-fold content.
      if (el.getBoundingClientRect().top < window.innerHeight * 0.9) return;
      el.classList.add('bk-reveal');
      io.observe(el);
    });

    // Fail-safe. A reveal animation is decoration; content being permanently
    // invisible is a broken page. If anything is still hidden 6s after load
    // and is at or above the fold, show it regardless of what the observer
    // did or didn't do. Nothing here should ever be the reason a customer
    // cannot see a review.
    setTimeout(function () {
      [].forEach.call(document.querySelectorAll('.bk-reveal:not(.bk-in)'), function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('bk-in');
      });
    }, 6000);
  }

  /* ------------------------------------------ 2. add-to-cart confirmation */

  function initAtcConfirm() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var btn = t.closest('.product-form__submit, button[name="add"], .quick-add__submit');
      if (!btn) return;
      if (REDUCED) return;
      btn.classList.remove('bk-atc-confirm');
      void btn.offsetWidth;
      btn.classList.add('bk-atc-confirm');
      setTimeout(function () { btn.classList.remove('bk-atc-confirm'); }, 260);
    }, true);

    // Bump the header cart count whenever the cart actually changes.
    var lastCount = null;
    function readCount() {
      var el = document.querySelector('.cart-count-bubble span:not(.visually-hidden), .cart-count-bubble');
      if (!el) return null;
      var n = parseInt((el.textContent || '').replace(/\D/g, ''), 10);
      return isNaN(n) ? null : n;
    }
    lastCount = readCount();
    var host = document.querySelector('#cart-icon-bubble') || document.body;
    if (!('MutationObserver' in window) || !host) return;
    new MutationObserver(function () {
      var n = readCount();
      if (n === null || n === lastCount) return;
      lastCount = n;
      if (REDUCED) return;
      var bubble = document.querySelector('.cart-count-bubble');
      if (!bubble) return;
      bubble.classList.remove('bk-cart-bump');
      void bubble.offsetWidth;
      bubble.classList.add('bk-cart-bump');
      setTimeout(function () { bubble.classList.remove('bk-cart-bump'); }, 400);
    }).observe(host, { subtree: true, childList: true, characterData: true });
  }

  /* --------------------------------------- 3. second image on product card */
  /* Interior and scale are the two things a cake buyer cannot judge from a
     hero shot. Hover on desktop is handled by the theme; this adds tap
     support on mobile, where hover does not exist.                          */

  function initCardSwap() {
    if (!window.matchMedia || !window.matchMedia('(hover: none)').matches) return;
    document.addEventListener('touchstart', function (e) {
      var card = e.target && e.target.closest ? e.target.closest('.card__media') : null;
      if (!card) return;
      var imgs = card.querySelectorAll('img');
      if (imgs.length < 2) return;
      card.classList.toggle('bk-swapped');
      imgs[0].style.opacity = card.classList.contains('bk-swapped') ? '0' : '1';
    }, { passive: true });
  }

  /* --------------------- 4. sticky add-to-cart with running price total */
  /*
   * The highest-value mechanic in the category research: the customer watches
   * the price BUILD from their own choices instead of being told it at the
   * end. Globo Product Options already computes a live total including option
   * surcharges and renders it into `.gpo-total`; we mirror that number rather
   * than recomputing it, so we can never disagree with the cart.
   */

  function parseMoney(text) {
    if (!text) return null;
    var m = String(text).replace(/[^0-9.,]/g, '').replace(/,/g, '');
    var n = parseFloat(m);
    return isNaN(n) ? null : n;
  }

  function formatMoney(n) {
    return '$' + n.toFixed(2).replace(/\.00$/, '');
  }

  function readLiveTotal() {
    // Prefer Globo's computed total (variant + selected option surcharges).
    var gpo = document.querySelector('.gpo-total, .gpo-total-text');
    var v = gpo ? parseMoney(gpo.textContent) : null;
    if (v) return v;
    // Fall back to the theme's variant price.
    var p = document.querySelector('.product__info-container .price-item--sale, .product__info-container .price-item--regular, .product__info-container .price-item');
    return p ? parseMoney(p.textContent) : null;
  }

  function animateNumber(el, from, to) {
    if (REDUCED || from === null || Math.abs(to - from) < 0.005) {
      el.textContent = formatMoney(to);
      return;
    }
    var start = null, dur = 220;
    function step(now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / dur);
      var eased = 1 - Math.pow(1 - t, 3);
      el.textContent = formatMoney(from + (to - from) * eased);
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initStickyAtc() {
    var realBtn = document.querySelector('.product-form__submit, button[name="add"]');
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!realBtn || !form) return;                       // not a product page
    var titleEl = document.querySelector('.product__title h1, .product__title, h1');
    var name = titleEl ? (titleEl.textContent || '').trim().slice(0, 60) : 'This cake';

    injectStyles();

    var bar = document.createElement('div');
    bar.id = 'bk-sticky-atc';
    bar.innerHTML =
      '<span class="bk-sa-meta">' +
        '<span class="bk-sa-name"></span>' +
        '<span class="bk-sa-total" aria-live="polite"></span>' +
      '</span>' +
      '<button type="button" class="bk-sa-btn">Add to cart</button>';
    bar.querySelector('.bk-sa-name').textContent = name;
    document.body.appendChild(bar);

    var totalEl = bar.querySelector('.bk-sa-total');
    var current = readLiveTotal();
    totalEl.textContent = current === null ? '' : formatMoney(current);

    // Clicking the sticky button defers entirely to the real one, so Globo's
    // validation (and our error-feedback fix) still runs exactly as normal.
    bar.querySelector('.bk-sa-btn').addEventListener('click', function () {
      realBtn.click();
    });

    // Show the bar only once the real button has scrolled out of view.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          bar.classList.toggle('bk-show', !en.isIntersecting);
        });
      }, { threshold: 0 }).observe(realBtn);
    } else {
      bar.classList.add('bk-show');
    }

    // Keep the total in step with the customer's choices.
    var poll = null;
    function sync() {
      var v = readLiveTotal();
      if (v === null || v === current) return;
      animateNumber(totalEl, current, v);
      current = v;
    }
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        clearTimeout(poll);
        poll = setTimeout(sync, 90);
      });
      mo.observe(document.querySelector('.gpo-app') || form, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['class', 'value']
      });
    }
    form.addEventListener('change', function () { setTimeout(sync, 60); });
    // Globo mounts asynchronously; re-read once it has settled.
    setTimeout(sync, 1500);
    setTimeout(sync, 3000);
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    injectStyles();
    initReveal();
    initAtcConfirm();
    initCardSwap();
    initStickyAtc();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
